
import React, { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box,
  Grid,
  Card,
  Typography,
  useTheme,
  Avatar,
} from '@mui/material'
import { renderColorByString } from '../DataGrid/renderColorByString'
import { useChangeView } from '../../utils/useChangeView'
import { boardViewMapping } from '../../utils/boardViewMapping'
import { useLayoutContext } from '../../context/LayoutProvider/LayoutProvider'
import { handleFormatTypesString } from '../Forms/types'
import { formatDateString } from '../Tasks/taskUtil'
import AssigneeAvatars from '../DataGrid/components/AssigneeAvatars'
import { Task } from '../../types'
import { useDrawerContext } from 'context/DrawerProvider/DrawerProvider'
import { BoxScroller } from 'styles/elements/styleElements'
import { useUserItems } from 'api/aws/users/useUserItems'

interface AssigneeProps {
  image_512: string
}
interface ProgressProps {
  total: number
  done: number
  inProgress: number
  onHold: number
  needsReview: number
  needMoreInfo: number
  notStarted: number
}
// ✅ Define Task type
type FilterTypes = {
  isCreative: boolean
  isMarketing: boolean
  isCommandCenter: boolean
}
interface CommandCenterTasksProps {
  tasks: Task[]
  expanded: boolean
}

const CommandCenterTasks: React.FC<CommandCenterTasksProps> = ({ tasks, expanded }) => {
  const { PK, SK } = useParams()
  const { pinned } = useLayoutContext()
  const navigate = useNavigate()
  const handleChangeView = useChangeView()
  const { closeNcoDrawer } = useDrawerContext()
  const { data: users, isLoading: isUsersLoading } = useUserItems()
  const tasksWithAssignees = useMemo(() => {
    if (!tasks || !users || isUsersLoading) return []
    return tasks?.map(t => {
      const assigneeIds = (t?.assignees as any)?.map((t: any) => t?.user_uuid)
      return { ...t, assignees: users?.filter(u => assigneeIds?.includes(u?.user_uuid)) ?? [] }
    })
  }, [tasks, users, isUsersLoading])
  const theme = useTheme()
  const handleNavigate = (task: Task) => {
    const route = '/teamtasks'
    const url = task?.SK?.startsWith('SUBTASK_')
      ? `/teamtasks/${task.PK}/${task.parentTaskSK}/subtasks`
      : `/teamtasks/${task.PK}/${task.SK}/main`
    const newView = boardViewMapping[route]
    if (newView) {
      handleChangeView({ newView })
    }
    closeNcoDrawer()
    navigate(url)
  }
  if (!tasks.length) return <Typography>No tasks found for this organization</Typography>

  return (
    <BoxScroller
      sx={{
        padding: 2,
        height: expanded ? `calc(100vh - 315px)` : `calc(100vh - 175px)`,
        transition: 'height 0.3s ease',
        overflowY: 'auto',
      }}
    >
      <Grid container spacing={2}>
        {tasksWithAssignees?.map((task: any) => {
          return (
            <Grid item xs={12} key={task.SK}>
              <Card
                sx={{
                  color: theme.palette.text.primary,
                  bgcolor: theme.palette.background.paper,
                  p: 2,
                  boxShadow:
                    theme.palette.mode === 'dark'
                      ? '0px 3px 10px rgba(255,255,255,0.1)'
                      : '0px 3px 10px rgba(0,0,0,0.1)',
                  transition: 'box-shadow 0.2s ease',
                  '&:hover': {
                    boxShadow: '0px 8px 20px rgba(0,0,0,0.3)',
                  },
                }}
              >
                <Box onClick={() => handleNavigate(task)} sx={{ cursor: 'pointer' }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={3}>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        sx={{
                          textAlign: 'left',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {task.name}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <Typography
                        sx={{ color: renderColorByString(handleFormatTypesString(task?.type)), textAlign: 'left' }}
                      >
                        {handleFormatTypesString(task?.type)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <Typography sx={{ color: renderColorByString(task?.masterStatus), textAlign: 'left' }}>
                        {task.masterStatus}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Typography variant="body2" sx={{ textAlign: 'left' }}>
                        {task?.startDate ? formatDateString(task.startDate) : ''}
                      </Typography>
                      <Typography variant="body2" sx={{ textAlign: 'left' }}>
                        {task?.endDate ? formatDateString(task.endDate) : ''}
                      </Typography>
                    </Grid>
                    {/* <Grid item xs={6} sm={2}>
                      <Typography variant='body2' sx={{ textAlign: 'left' }}>
                        {task?.endDate ? formatDateString(task.endDate) : ''}
                      </Typography>
                    </Grid> */}
                    <Grid item xs={12} sm={1} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      {task?.assignees && (task?.assignees as any[]).length > 0 ? (
                        <AssigneeAvatars assignees={task?.assignees as any} id={task?.SK} />
                      ) : (
                        <Avatar sx={{ width: 28, height: 28, fontSize: 12, opacity: 0.3 }} />
                      )}
                    </Grid>
                    {/* <Grid item xs={12} sm={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <RenderProgressChart task={task} />
                    </Grid> */}
                  </Grid>
                </Box>
              </Card>
            </Grid>
          )
        })}
      </Grid>
    </BoxScroller>
  )
}

export default CommandCenterTasks
